import { useState, useEffect, useRef, useCallback } from "react";
import { useMapsLibrary, useMap } from "@vis.gl/react-google-maps";
import { Input } from "@/components/ui/input";

interface PlaceSearchInputProps {
  onCitySelected?: (name: string) => void;
}

export function PlaceSearchInput({ onCitySelected }: PlaceSearchInputProps) {
  const placesLib = useMapsLibrary("places");
  const map = useMap();

  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);

  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(
    null,
  );
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null,
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Create services once placesLib loads
  useEffect(() => {
    if (!placesLib || !map) return;
    serviceRef.current = new placesLib.AutocompleteService();
    placesServiceRef.current = new placesLib.PlacesService(map);
  }, [placesLib, map]);

  const handleInputChange = useCallback(
    (value: string) => {
      setQuery(value);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (!value.trim() || !serviceRef.current) {
        setPredictions([]);
        setIsOpen(false);
        return;
      }

      debounceRef.current = setTimeout(() => {
        serviceRef.current!.getPlacePredictions(
          { input: value, types: ["(cities)"] },
          (results, status) => {
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              results
            ) {
              setPredictions(results);
              setIsOpen(true);
            } else {
              setPredictions([]);
              setIsOpen(false);
            }
          },
        );
      }, 300);
    },
    [],
  );

  const handleSelect = useCallback(
    (prediction: google.maps.places.AutocompletePrediction) => {
      if (!placesServiceRef.current || !map) return;

      setQuery(prediction.description);
      setPredictions([]);
      setIsOpen(false);

      placesServiceRef.current.getDetails(
        { placeId: prediction.place_id, fields: ["geometry", "name"] },
        (place, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            place?.geometry?.location
          ) {
            map.panTo(place.geometry.location);
            map.setZoom(13);

            if (onCitySelected && place.name) {
              onCitySelected(place.name);
            }
          }
        },
      );
    },
    [map, onCitySelected],
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  if (!placesLib || !map) return null;

  return (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 w-72">
      <Input
        type="text"
        placeholder="Search for a city..."
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => {
          if (predictions.length > 0) setIsOpen(true);
        }}
        onBlur={() => {
          // Delay closing so click on dropdown registers
          setTimeout(() => setIsOpen(false), 200);
        }}
        className="bg-white text-gray-900 placeholder:text-gray-500 shadow-md"
      />
      {isOpen && predictions.length > 0 && (
        <ul className="mt-1 bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden">
          {predictions.map((prediction) => (
            <li
              key={prediction.place_id}
              className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 cursor-pointer"
              onMouseDown={() => handleSelect(prediction)}
            >
              {prediction.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
